import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ExternalLink, Lock, Target, Compass, BookOpen, Users, Sparkles,
  CheckCircle2, X,
} from "lucide-react";
import { COLORS } from "@/lib/api";

const NOTION_URL =
  "https://app.notion.com/p/Laboratorio-de-Incidencia-P-blica-2ba4ae21b26d804ab9c9f166d27df5c6";
const MATERIAL_PASSWORD = "LABIP2026C";

const ETAPAS = [
  { t: "Conocimientos", d: "Análisis de problemas públicos, teoría de cambio y herramientas para la elaboración de propuestas legislativas y de política pública.", m: "6 meses", c: COLORS.magenta },
  { t: "Habilidades", d: "Comunicación estratégica para participar en mecanismos de participación social y vincularse con personas tomadoras de decisiones.", m: "2 meses", c: COLORS.orange },
  { t: "Aptitudes", d: "Gestión emocional, liderazgo colectivo y administración ética del tiempo, los recursos y el trabajo.", m: "2 meses", c: COLORS.lime },
  { t: "Evaluación y acompañamiento", d: "Seguimiento continuo para consolidar capacidades e integrar enfoques éticos y transversales, considerando la diversidad de personas, sectores y territorios.", m: "2 meses", c: COLORS.yellow },
];

const CONTENIDOS = [
  "Comprensión de los problemas públicos desde una perspectiva crítica y situada.",
  "Herramientas para el análisis, la planeación y el diseño de estrategias de incidencia pública.",
  "Contexto político, espacios de decisión en México y mecanismos de participación ciudadana.",
  "Desarrollo de narrativas y habilidades de comunicación para la incidencia pública.",
  "Gestión emocional, liderazgo colectivo y cuidado en procesos de participación.",
  "Evaluación y sostenibilidad de las estrategias de incidencia a lo largo del tiempo.",
];

const DIFERENCIA = [
  "Es un proceso de largo aliento (no un taller aislado), con acompañamiento sostenido por las organizaciones convocantes.",
  "Se centra en la experiencia de las participantes: sus contextos, trayectorias y preguntas son el punto de partida.",
  "Integra herramientas prácticas con una dimensión ética y de cuidado (emociones, tiempos y vínculos).",
  "Se nutre de la experiencia de organizaciones de la sociedad civil, asumiendo el error como parte del aprendizaje.",
  "Promueve el liderazgo colectivo: redes y procesos compartidos por encima de lógicas individuales o extractivas.",
];

const RESULTADOS = [
  "Una agenda de incidencia clara y priorizada, construida desde sus intereses y contextos.",
  "Un plan de incidencia básico y aplicable para incidir en el espacio público.",
  "Mayor claridad sobre cómo dialogar con instituciones y usar mecanismos de participación ciudadana.",
  "Herramientas para analizar problemas públicos desde una mirada colectiva y situada.",
  "Vínculos fortalecidos con otras juventudes de distintos territorios.",
];

const TEMAS = ["Cuidados", "Salud mental", "Movilidad", "Acción climática", "Economía y trabajo"];

const fade = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay: i * 0.05 } }),
};

const Section = ({ icon: Icon, kicker, title, children }) => (
  <motion.section
    initial="hidden"
    whileInView="show"
    viewport={{ once: true }}
    variants={fade}
    className="mt-14"
  >
    <div className="flex items-center gap-2 text-brand-magenta">
      {Icon && <Icon size={18} />}
      <span className="text-sm uppercase tracking-[0.15em] font-semibold">{kicker}</span>
    </div>
    {title && (
      <h2 className="mt-3 font-heading text-2xl md:text-3xl font-semibold tracking-tight text-brand-ink">
        {title}
      </h2>
    )}
    {children}
  </motion.section>
);

const Laboratorio = () => {
  const [showGate, setShowGate] = useState(false);
  const [pass, setPass] = useState("");
  const [error, setError] = useState(false);

  const unlock = (e) => {
    e.preventDefault();
    if (pass.trim() === MATERIAL_PASSWORD) {
      window.open(NOTION_URL, "_blank", "noopener,noreferrer");
      setShowGate(false);
      setPass("");
      setError(false);
    } else {
      setError(true);
    }
  };

  return (
    <div data-testid="programa-laboratorio">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E5E5E5]">
        <div className="absolute inset-0">
          <img
            src="/lab-header.jpg"
            alt="Laboratorio de Incidencia Pública"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(17,17,17,0.5), rgba(17,17,17,0.85))" }} />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Link to="/programas" data-testid="programa-back" className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white mb-6">
            <ArrowLeft size={16} /> Programas
          </Link>
          <span className="inline-block w-16 h-1.5 rounded-sm mb-6" style={{ background: COLORS.magenta }} />
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[0.98]">
            Laboratorio de Incidencia Pública
          </h1>
          <p className="mt-5 text-lg md:text-xl text-white/85 font-medium max-w-2xl">
            Un proceso formativo para que las juventudes de todo el país incidan en las decisiones que
            afectan sus vidas y sus comunidades.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        {/* Qué es */}
        <Section icon={Target} kicker="El programa" title="¿Qué es el Laboratorio?">
          <div className="mt-5 space-y-4 text-brand-ink/80 leading-relaxed">
            <p>El Laboratorio de Incidencia Estratégica para Juventudes es un proceso formativo digital que fortalece los conocimientos, habilidades y capacidades de jóvenes de todo el país para participar en la toma de decisiones públicas. Es un espacio ciudadano y apartidista que, a través del aprendizaje colectivo y herramientas prácticas, impulsa la comprensión, el diseño y la sostenibilidad de mecanismos de participación social y agendas de incidencia pública, desde un enfoque ético, crítico y colaborativo.</p>
            <p>Las juventudes (entre 13 y 24 años) desarrollan propuestas de incidencia pública —proyectos, iniciativas y demandas— dirigidas a gobiernos y otros actores públicos. No es un curso aislado: las participantes analizan problemas públicos, construyen una agenda de incidencia y definen rutas de acción reales, sostenidas en el tiempo.</p>
            <p>En términos de valor público, el Laboratorio contribuye a fortalecer el tejido democrático del país al sumar más voces jóvenes, informadas y críticas al debate público; promueve formas de diálogo más empáticas y constructivas, y ayuda a contrarrestar la polarización, la desinformación y los discursos de odio.</p>
          </div>
        </Section>

        {/* Primera Edición 2026 */}
        <Section icon={Sparkles} kicker="Primera edición 2026" title="Así arrancó el Laboratorio">
          <p className="mt-4 text-brand-ink/80 leading-relaxed">
            El programa combina <span className="font-semibold text-brand-ink">formación política aplicada</span> con{" "}
            <span className="font-semibold text-brand-ink">acompañamiento práctico</span>, permitiendo que las
            participantes diseñen e implementen proyectos reales de incidencia en sus contextos.
          </p>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { n: "12", l: "Meses de duración", sub: "6 teóricos y 6 de implementación", c: COLORS.magenta },
              { n: "5", l: "Módulos teóricos", c: COLORS.orange },
              { n: "116", l: "Participantes activas", c: COLORS.lime },
              { n: "10", l: "Iniciativas resultantes", c: COLORS.magenta },
              { n: "22", l: "Estados representados", c: COLORS.yellow },
            ].map((s) => (
              <div key={s.l} className="bg-white border border-[#E5E5E5] rounded-sm p-6 text-center">
                <span className="font-heading text-5xl font-bold" style={{ color: s.c }}>{s.n}</span>
                <p className="mt-2 font-semibold text-brand-ink leading-snug">{s.l}</p>
                {s.sub && <p className="mt-1 text-xs text-brand-ink/50">{s.sub}</p>}
              </div>
            ))}
          </div>
          <div className="mt-6 space-y-4 text-brand-ink/80 leading-relaxed">
            <p>
              Esta primera edición se enfocó en <span className="font-semibold text-brand-ink">fortalecer las iniciativas que ya se desarrollan desde los Clubs de Juventudes Latentes</span>, potenciando su capacidad de incidencia y organización.
            </p>
            <p>
              Al mismo tiempo, <span className="font-semibold text-brand-ink">el proceso se abrió a participantes fuera de la red</span>, lo que permitió construir una comunidad más amplia y diversa, con presencia de personas de entre 15 y 49 años, enriqueciendo el diálogo intergeneracional.
            </p>
            <p>
              Este enfoque es algo que buscamos mantener y ampliar en futuras ediciones: un espacio abierto a juventudes y a la comunidad en general, más allá de los Clubs.
            </p>
          </div>
        </Section>

        {/* Etapas */}
        <Section icon={Compass} kicker="Formación" title="Etapas de capacitación">
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {ETAPAS.map((e, i) => (
              <div key={e.t} className="bg-white border border-[#E5E5E5] rounded-sm p-6">
                <div className="flex items-center justify-between">
                  <span className="w-9 h-9 rounded-sm flex items-center justify-center font-heading font-bold text-white" style={{ background: e.c }}>{i + 1}</span>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-sm" style={{ background: e.c, color: "#111" }}>{e.m}</span>
                </div>
                <h3 className="mt-4 font-heading text-lg font-semibold tracking-tight text-brand-ink">{e.t}</h3>
                <p className="mt-2 text-sm text-brand-ink/70 leading-relaxed">{e.d}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Herramientas (diagrama) */}
        <Section icon={Sparkles} kicker="Metodología" title="Las 12 herramientas del Laboratorio">
          <p className="mt-4 text-brand-ink/70 leading-relaxed">
            La metodología privilegia el <span className="font-semibold text-brand-ink">aprender haciendo</span>: exposiciones breves, ejercicios prácticos, trabajo en grupos por territorio y reflexión colectiva, organizados en cuatro ejes de trabajo.
          </p>
          <div className="mt-6 bg-white border border-[#E5E5E5] rounded-sm p-4">
            <img src="/lab-proceso.webp" alt="Herramientas del Laboratorio de Incidencia" className="w-full rounded-sm" />
          </div>
        </Section>

        {/* Contenidos */}
        <Section icon={BookOpen} kicker="Contenidos" title="Lo que aprenderás">
          <ul className="mt-6 space-y-3">
            {CONTENIDOS.map((c) => (
              <li key={c} className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-brand-magenta flex-shrink-0 mt-0.5" />
                <span className="text-brand-ink/80">{c}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* Temas */}
        <Section icon={Sparkles} kicker="Agendas" title="Temas que se trabajan">
          <p className="mt-4 text-brand-ink/70">Las propuestas parten de problemáticas reales en los territorios de las participantes:</p>
          <div className="mt-5 flex flex-wrap gap-3">
            {TEMAS.map((t, i) => (
              <span key={t} className="px-4 py-2 rounded-sm font-semibold text-sm text-white" style={{ background: [COLORS.magenta, COLORS.orange, COLORS.lime, COLORS.yellow, COLORS.magenta][i] }}>
                {t}
              </span>
            ))}
          </div>
        </Section>

        {/* A quién */}
        <Section icon={Users} kicker="Participantes" title="¿A quién está dirigido?">
          <p className="mt-5 text-brand-ink/80 leading-relaxed">
            A juventudes interesadas en comprender e incidir en lo público desde sus propios contextos y experiencias, especialmente quienes buscan fortalecer su participación desde un enfoque ético, colectivo y comprometido con el cambio social. Es un espacio para quienes tienen preguntas, inquietudes o experiencias de participación social y quieren transformarlas en procesos de incidencia más claros, sostenidos y con impacto.
          </p>
        </Section>

        {/* Diferencia */}
        <Section icon={Sparkles} kicker="Nuestro sello" title="¿Qué lo hace diferente?">
          <ul className="mt-6 space-y-3">
            {DIFERENCIA.map((d) => (
              <li key={d} className="flex items-start gap-3">
                <span className="mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: COLORS.orange }} />
                <span className="text-brand-ink/80">{d}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* Resultados */}
        <Section icon={CheckCircle2} kicker="Resultados" title="¿Qué esperamos de este proceso?">
          <p className="mt-4 text-brand-ink/70">Al finalizar, las participantes contarán con:</p>
          <ul className="mt-4 space-y-3">
            {RESULTADOS.map((r) => (
              <li key={r} className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-brand-lime flex-shrink-0 mt-0.5" />
                <span className="text-brand-ink/80">{r}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-brand-ink/70 leading-relaxed">
            Como resultado, se busca desarrollar al menos <span className="font-semibold text-brand-ink">cinco acciones de incidencia pública</span> impulsadas por juventudes en distintas entidades de la República, de acuerdo con sus contextos locales.
          </p>
        </Section>

        {/* Quién lo impulsa */}
        <Section icon={Users} kicker="Alianza" title="¿Quién impulsa el Laboratorio?">
          <p className="mt-5 text-brand-ink/80 leading-relaxed">
            El Laboratorio es impulsado por <span className="font-semibold text-brand-ink">BORDE</span> y <span className="font-semibold text-brand-ink">Juventudes Latentes</span>. BORDE aporta experiencia en el análisis de problemas públicos, el diseño de metodologías participativas y la construcción de estrategias de incidencia y articulación política. Juventudes Latentes aporta su trabajo con juventudes orientado a la formación, el acompañamiento y la construcción de redes para promover la igualdad de género y la justicia social.
          </p>
          <a
            href="https://borde.mx"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="borde-link"
            className="mt-6 inline-flex items-center gap-4 bg-white border border-[#E5E5E5] rounded-sm p-5 hover:border-brand-magenta transition-colors"
          >
            <img src="/borde-logo.png" alt="BORDE" className="h-10 object-contain" />
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-brand-ink">
              Visita borde.mx <ExternalLink size={15} />
            </span>
          </a>
        </Section>

        {/* Material protegido */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fade}
          className="mt-16 bg-brand-ink rounded-sm p-8 md:p-10"
        >
          <span className="w-11 h-11 rounded-sm flex items-center justify-center" style={{ background: COLORS.lime }}>
            <Lock size={20} className="text-brand-ink" strokeWidth={2.2} />
          </span>
          <h2 className="mt-5 font-heading text-2xl md:text-3xl font-semibold tracking-tight text-white">
            Material de las sesiones
          </h2>
          <p className="mt-2 text-white/70 max-w-2xl">
            Espacio exclusivo para participantes del Laboratorio 2026. Ingresa la contraseña para
            acceder a los materiales.
          </p>
          <button
            onClick={() => setShowGate(true)}
            data-testid="open-material-gate"
            className="mt-6 inline-flex items-center gap-2 bg-white text-brand-ink font-semibold px-6 py-3 rounded-sm hover:bg-brand-lime transition-colors"
          >
            <Lock size={16} /> Acceder al material
          </button>
        </motion.div>
      </div>

      {/* Password modal */}
      {showGate && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6">
          <form onSubmit={unlock} data-testid="material-gate-form" className="bg-white rounded-sm w-full max-w-md p-7">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-xl font-semibold text-brand-ink">Material de las sesiones</h3>
              <button type="button" onClick={() => { setShowGate(false); setError(false); }} data-testid="close-gate"><X size={22} /></button>
            </div>
            <p className="mt-2 text-sm text-brand-ink/60">Ingresa la contraseña para participantes.</p>
            <input
              type="password"
              autoFocus
              data-testid="material-password"
              value={pass}
              onChange={(e) => { setPass(e.target.value); setError(false); }}
              placeholder="Contraseña"
              className="mt-4 w-full border border-[#E5E5E5] rounded-sm px-4 py-2.5 outline-none focus:border-brand-magenta"
            />
            {error && <p data-testid="gate-error" className="mt-2 text-sm text-red-600">Contraseña incorrecta. Intenta de nuevo.</p>}
            <button type="submit" data-testid="submit-password" className="mt-5 w-full bg-brand-ink text-white font-semibold py-3 rounded-sm hover:bg-brand-orange transition-colors">
              Acceder
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Laboratorio;
